import React, { createContext, useReducer, useContext } from "react";
import {
  MeDocument,
  useAudioToggleUserMutation,
  useHideLearningActivityIntroMutation,
} from "../graphql/generated/graphql";
import Sentry from "../utils/sentry";

type ExecutionInfo = {
  agentName: string | null;
  status: string | null;
  message: string | null;
};

type AppStateMeta = {
  isAdventure: boolean | null;
  isExpanded: boolean;
  isModalOpen: boolean | null;
  isGenerated: boolean | null;
  abort: boolean;
  activeMessages: number;
  generationLabel: string;
  welcomeStep: boolean;
  keypressIsListening: boolean;
  isAudioOn: boolean;
  executionInfo: ExecutionInfo;
  isLearnMenuOpen: boolean;
  isCustomCourseworkMenuOpen: boolean;
  isMainMenuExpanded: boolean;
  completedAiTutorId: string | null;
  showLearningActivityIntro?: boolean;
};

type LearningState = {
  state: string | null;
  topic: unknown;
  topicAncestors: unknown[];
  label: string | null;
  isUnitAssessment: boolean;
  subchapterName: string | null;
  subchapterDescription: string | null;
  objectives: unknown;
};

type MediaState = {
  video: unknown[];
  wiki: unknown[];
};

type AppStateState = {
  general: string;
  media: MediaState;
  learning: LearningState;
  view?: string | null;
};

type AudioState = {
  type: string | null;
  data: unknown;
};

type CurrentMessage = { content?: unknown } & Record<string, unknown>;

export type AppState = {
  meta: AppStateMeta;
  state: AppStateState;
  audio: AudioState;
  content: Record<string, unknown>;
  conversation: unknown[];
  currentMessage: CurrentMessage | null;
};

export const initialState: AppState = {
  meta: {
    isAdventure: null,
    isExpanded: false,
    isModalOpen: null,
    isGenerated: null,
    abort: false,
    activeMessages: 0,
    generationLabel: "",
    welcomeStep: false,
    keypressIsListening: true,
    isAudioOn: true,
    executionInfo: {
      agentName: null,
      status: null,
      message: null,
    },
    isLearnMenuOpen: false,
    isCustomCourseworkMenuOpen: false,
    isMainMenuExpanded: false,
    completedAiTutorId: null,
  },
  state: {
    general: "GENERAL_CONVERSATION",
    media: {
      video: [],
      wiki: [],
    },
    learning: {
      state: null,
      topic: null,
      topicAncestors: [],
      label: null,
      isUnitAssessment: false,
      subchapterName: null,
      subchapterDescription: null,
      objectives: null,
    },
  },
  audio: {
    type: null,
    data: null,
  },
  content: {},
  conversation: [],
  currentMessage: null,
};

export const actions = {
  SET_META: "SET_META",
  SET_MEDIA: "SET_MEDIA",
  SET_GENERAL_STATE: "SET_GENERAL_STATE",
  SET_LEARNING_STATE: "SET_LEARNING_STATE",
  SET_LEARNING_TOPIC: "SET_LEARNING_TOPIC",
  SET_LEARNING_TOPIC_ANCESTORS: "SET_LEARNING_TOPIC_ANCESTORS",
  SET_LEARNING_LABEL: "SET_LEARNING_LABEL",
  SET_LEARNING_ASSESSMENT_DETAILS: "SET_LEARNING_ASSESSMENT_DETAILS",
  SET_VIEW: "SET_VIEW",
  RESET_STATE: "RESET_STATE",
  SET_CONTENT: "SET_CONTENT",
  SET_CONVERSATION: "SET_CONVERSATION",
  SET_CURRENT_MESSAGE: "SET_CURRENT_MESSAGE",
  UPDATE_CURRENT_MESSAGE: "UPDATE_CURRENT_MESSAGE",
  RESET_CURRENT_MESSAGE: "RESET_CURRENT_MESSAGE",
  SET_AUDIO: "SET_AUDIO",
  RESET_COMPLETED_AITUTOR_ID: "RESET_COMPLETED_AITUTOR_ID",
} as const;

type AppStateAction = {
  type: (typeof actions)[keyof typeof actions];
  payload?: unknown;
};

type AppStateContextValue = {
  state: AppState;
  dispatch: React.Dispatch<AppStateAction>;
  handleAudioOffPress: () => void;
  handleAudioOnPress: () => void;
  handleHideLearningActivityIntro: () => void;
};

type AppStateProviderProps = {
  children: React.ReactNode;
};

const mergeDefinedProps = <T extends Record<string, unknown>>(obj1: T, obj2: Partial<T>) => {
  const result = { ...obj1 };
  for (const [key, value] of Object.entries(obj2)) {
    if (value !== undefined && value !== null) {
      result[key] = value;
    }
  }
  return result;
};

const reducer = (state: AppState, action: AppStateAction): AppState => {
  switch (action.type) {
    case actions.SET_META:
      return { ...state, meta: mergeDefinedProps(state.meta, action.payload as Partial<AppStateMeta>) };
    case actions.SET_MEDIA:
      return {
        ...state,
        state: {
          ...state.state,
          media: action.payload as MediaState,
        },
      };
    case actions.SET_GENERAL_STATE:
      return { ...state, state: { ...state.state, general: action.payload as string } };
    case actions.SET_LEARNING_STATE:
      return {
        ...state,
        state: {
          ...state.state,
          learning: { ...state.state.learning, state: action.payload as LearningState["state"] },
        },
      };
    case actions.SET_LEARNING_TOPIC:
      return {
        ...state,
        state: {
          ...state.state,
          learning: { ...state.state.learning, topic: action.payload as LearningState["topic"] },
        },
      };
    case actions.SET_LEARNING_TOPIC_ANCESTORS:
      return {
        ...state,
        state: {
          ...state.state,
          learning: { ...state.state.learning, topicAncestors: action.payload as LearningState["topicAncestors"] },
        },
      };
    case actions.SET_LEARNING_LABEL:
      return {
        ...state,
        state: {
          ...state.state,
          learning: { ...state.state.learning, label: action.payload as LearningState["label"] },
        },
      };
    case actions.SET_LEARNING_ASSESSMENT_DETAILS:
      return {
        ...state,
        state: {
          ...state.state,
          learning: { ...state.state.learning, ...(action.payload as Partial<LearningState>) },
        },
      };
    case actions.SET_VIEW:
      return { ...state, state: { ...state.state, view: action.payload as AppStateState["view"] } };
    case actions.SET_CONTENT:
      return {
        ...state,
        content: { ...state.content, ...(action.payload as Record<string, unknown>) },
      };
    case actions.SET_CURRENT_MESSAGE:
      return {
        ...state,
        currentMessage: action.payload as CurrentMessage | null,
      };
    case actions.UPDATE_CURRENT_MESSAGE:
      return {
        ...state,
        currentMessage: {
          ...(state.currentMessage ?? {}),
          content: action.payload,
        },
      };
    case actions.RESET_CURRENT_MESSAGE:
      return {
        ...state,
        currentMessage: null,
      };
    case actions.SET_CONVERSATION:
      return {
        ...state,
        conversation: action.payload as AppState["conversation"],
      };
    case actions.SET_AUDIO:
      return {
        ...state,
        audio: action.payload as AudioState,
      };
    case actions.RESET_COMPLETED_AITUTOR_ID:
      return {
        ...state,
        meta: {
          ...state.meta,
          completedAiTutorId: null,
        },
      };
    case actions.RESET_STATE:
      return { ...initialState };

    default:
      throw new Error(`Unknown action: ${action.type}`);
  }
};

export const AppStateContext = createContext<AppStateContextValue | null>(null);

export const AppStateProvider = ({ children }: AppStateProviderProps) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const [toggleAudio] = useAudioToggleUserMutation();
  const [hideLearningActivityIntroModal] = useHideLearningActivityIntroMutation({
    refetchQueries: [MeDocument],
  });

  const handleAudioOffPress = () => {
    dispatch({
      type: actions.SET_META,
      payload: { isAudioOn: false },
    });

    toggleAudio({
      variables: {
        audioOn: false,
      },
    }).catch((error) => {
      Sentry.captureException(error);
      // Revert the state change on error
      dispatch({
        type: actions.SET_META,
        payload: { isAudioOn: true },
      });
    });
  };

  const handleAudioOnPress = () => {
    dispatch({
      type: actions.SET_META,
      payload: { isAudioOn: true },
    });

    toggleAudio({
      variables: {
        audioOn: true,
      },
    }).catch((error) => {
      Sentry.captureException(error);
      // Revert the state change on error
      dispatch({
        type: actions.SET_META,
        payload: { isAudioOn: false },
      });
    });
  };

  const handleHideLearningActivityIntro = () => {
    dispatch({
      type: actions.SET_META,
      payload: { showLearningActivityIntro: false },
    });

    hideLearningActivityIntroModal().catch((error) => {
      Sentry.captureException(error);
      // Revert the state change on error
      dispatch({
        type: actions.SET_META,
        payload: { showLearningActivityIntro: true },
      });
    });
  };

  return (
    <AppStateContext.Provider
      value={{ state, dispatch, handleAudioOffPress, handleAudioOnPress, handleHideLearningActivityIntro }}>
      {children}
    </AppStateContext.Provider>
  );
};

export const useAppState = () => {
  const context = useContext(AppStateContext);
  if (!context) {
    throw new Error("useAppState must be used within AppStateProvider");
  }
  return context;
};
